import React, {useEffect, useRef, useState} from 'react';
import {WdiModalAppProps} from '@/components/WdiControls';
import {WdiModalForm} from '@/components/WdiControls/WdiModalForm';
import {
  Button,
  Col,
  Form,
  Input,
  message,
  Radio,
  RadioChangeEvent,
  Row,
  TableColumnsType,
  Tooltip,
} from 'antd';
import {ProFormInstance} from '@ant-design/pro-form';
import {WdiSelect, WdiSelectFormat} from '@/components/WdiControls/WdiSelect';
import {RejectReason} from '@/services/flow/rejectreason';
import {ProcessInstance} from '@/services/flow';
import {formatter, request, ResponseStatus} from '@/utils/request';
import {WdiTable} from '@/components/WdiControls/WdiTable';
import DataStore from '@/components/GridWidget/DataStore';
import {getParameter} from './data';
import {TableColumnRanderFormats} from '@/components/WdiBus/TableColumnRanderFormats';
import {Position} from "@/services/basic/position";
import {Enum} from "@/enums/system";

export const ApprovalModalType = {
  Approve: 1,
  IntervalFlow: 2,
};

const FlowApproveType = {
  Approve: "0",
  InterveneError: "1",
  InterveneUser: "2",
  InterveneAll: "3"
}

const ApprovalModalApp: React.FC<WdiModalAppProps> = (props) => {

  const formRef = useRef<ProFormInstance>();

  const [loading, setLoading] = useState(false);

  const [buttonLoading, setButtonLoading] = useState(false);

  const [approveStatus, setApproveStatus] = useState<string>("1");

  const [title, setTitle] = useState<string>('');

  const [dataSource, setDataSource] = useState<API.FlowNode[]>([]);

  const [currentFlowUsers, setCurrentFlowUsers] = useState<any[]>([]);
  const [viewLimitedRemark, setViewLimitedRemark] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      let processId = props?.data?.form?.processId || 0;
      const responsePermissions = await Position.access({
        useCaseId: Enum.UseCase.BATCH_APPROVE,
        operateTypeIds: [Enum.OperateType.REMARK_NEED_PERMISSION]
      });

      if (responsePermissions && responsePermissions.code == ResponseStatus.SUCCESS && responsePermissions.data == true) {
        setViewLimitedRemark(true)
      }

      ProcessInstance.getFlowUsers(processId).then(response => {
        var approversWaitingFor = '';
        if (response.code === ResponseStatus.SUCCESS && response.data) {
          response.data.forEach((item, rowIndex) => {
            if (rowIndex > 1) {
              return false;
            }
            approversWaitingFor += item.staffName;
            approversWaitingFor += ',';
            return true;
          })
          approversWaitingFor = approversWaitingFor.substring(0, approversWaitingFor.length - 1);
          if (response.data.length > 2) {
            approversWaitingFor += '…';
          }
          if (props?.type == ApprovalModalType.IntervalFlow) {
            setCurrentFlowUsers(response.data?.map(item => {
              return {label: item.staffName, value: item.staffId}
            }))
            if (response.data?.length > 0) {
              formRef.current?.setFieldsValue({
                approveType: FlowApproveType.InterveneUser,
              });
              if (response.data?.length == 1) {
                formRef.current?.setFieldsValue({
                  interveneUserId: response.data[0].staffId,
                });
              }
            } else {
              formRef.current?.setFieldsValue({
                approveType: FlowApproveType.InterveneError,
              });
            }
          }
        }
        setTitle('等待' + approversWaitingFor + '审批');
      });

      ProcessInstance.getFlowTrace(processId).then(response => {
        setDataSource(DataStore.init(response?.data));
      })

      setLoading(false);
    })();
  }, [props.data]);

  const onRadioChange = ({target: {value}}: RadioChangeEvent) => {
    setApproveStatus(value);
    if (value !== '2') {
      formRef.current?.setFieldsValue({
        rejectReason: undefined
      });
    }
    if (props?.type == ApprovalModalType.IntervalFlow) {
      if (value === '2') {
        formRef.current?.setFieldsValue({
          approveType: FlowApproveType.InterveneAll,
        });
      } else {
        if (currentFlowUsers && currentFlowUsers.length) {
          formRef.current?.setFieldsValue({
            approveType: FlowApproveType.InterveneUser,
          });
        } else {
          formRef.current?.setFieldsValue({
            approveType: FlowApproveType.InterveneError,
          });
        }
      }
    }

    // if (value !== '4') {
    //   formRef.current?.setFieldsValue({
    //     returnToUserId: undefined
    //   });
    // }
  }

  const columns: TableColumnsType<API.FlowNode> = [
    {
      title: '操作人',
      width: 140,
      dataIndex: 'approver',
      ellipsis: true,
    },
    {
      title: '审批类型',
      width: 90,
      dataIndex: 'approveType',
      ellipsis: true,
    },
    {
      key: "createdDate",
      dataIndex: "createdDate",
      title: "到达时间",
      width: 125,
      render: TableColumnRanderFormats.ToMinutes,
    },
    {
      key: "remark",
      dataIndex: "remark",
      title: "审批意见",
      ellipsis: {
        showTitle: false,
      },
      render: (value: any) => (
        <Tooltip placement="bottomLeft" title={value}>
          {value}
        </Tooltip>
      ),
    },
    // 财务评估审批意见(仅总监可见)：仅有权限时显示该列
    ...(viewLimitedRemark
      ? ([{
        key: 'remarkNeedPermission',
        dataIndex: 'remarkNeedPermission',
        title: '财务评估审批意见',
        width: 230,
        ellipsis: {
          showTitle: false,
        },
        render: (_: any, record: any) => {
          const value = record?.limitedRemark ?? record?.remarkLimited ?? record?.remarkNeedPermission;
          return (
            <Tooltip placement="bottomLeft" title={value}>
              {value}
            </Tooltip>
          );
        },
      }] as TableColumnsType<API.FlowNode>)
      : [])
  ];

  const submit = async (entity: any) => {
    const {data} = props;
    const args = getParameter(data);
    setButtonLoading(true);
    let isAgree;

    if (entity.approveStatus === "1" || entity.approveStatus === "4") {
      isAgree = "true";
      if (entity.approveStatus === "4") {
        if (entity.returnToUserId) {
          args.items.push({name: "returnToUserId", value: WdiSelectFormat.getvalue(entity.returnToUserId)});
        } else {
          message.error("退回至用户不能为空");
          setButtonLoading(false);
          return;
        }
      }
    } else {
      isAgree = "false";
    }

    args.items.push({name: "isAgree", value: isAgree});
    args.items.push({name: "approvalReason", value: entity.approvalReason});
    // 财务评估审批意见(仅总监可见)：选填
    args.items.push({name: "remarkNeedPermission", value: entity.remarkNeedPermission});

    args.items.push({name: "source", value: "电脑端审批"});
    let approveType = entity.approveType;
    if (!entity.approveType) {
      approveType = FlowApproveType.Approve;
    }
    args.items.push({name: "approveType", value: approveType});
    if (approveType == FlowApproveType.InterveneUser) {
      if (entity.interveneUserId) {
        args.items.push({name: "interveneUserId", value: entity.interveneUserId});
      } else {
        message.error("请选择需要干预的审批人");
        setButtonLoading(false);
        return;
      }
    }

    const response = await request(data.url, formatter({method: 'POST', body: args}));
    if (response.code == ResponseStatus.SUCCESS) {
      message.success('审批成功');
      props.onSave?.({code: ResponseStatus.SUCCESS, data: data});
    } else {
      message.error(response.message);
      setButtonLoading(false);
    }
  }

  return (
    <WdiModalForm
      formRef={formRef}
      initialValues={{
        approveStatus: approveStatus
      }}
      labelAlign='left'
      layout="horizontal"
      title={title}
      visible={props.visible}
      width={880}
      modalProps={{
        onCancel: () => {
          props.onClose?.()
        },
      }}
      submitter={{
        render: () => {
          return [
            <Button key="back" onClick={() => {
              props.onClose?.()
            }}>
              取消
            </Button>,
            <Button loading={buttonLoading} type="primary"
                    onClick={async () => {
                      const values = await formRef.current?.validateFieldsReturnFormatValue?.();
                      if (values && values.errorFields && values.errorFields.length) {
                        return;
                      }
                      const {...entity} = values;
                      if (entity.approveStatus === "2" && !entity.approvalReason) {
                        message.error('请输入审批拒绝的原因');
                      } else {
                        if (props?.data?.tooltip) {
                          if (window.confirm(props?.data?.tooltip)) {
                            await submit(entity);
                          }
                        } else {
                          await submit(entity);
                        }
                      }
                    }}
            >
              确认
            </Button>,
          ];
        },
      }}
    >
      <Row gutter={[0, 0]}>
        <Col span={9}>
          <Form.Item name="approveStatus">
            <Radio.Group onChange={onRadioChange}>
              <Tooltip title="流程继续审批">
                <Radio value="1">同意</Radio>
              </Tooltip>
              <Tooltip title="退回给申请人重新编辑后重走流程">
                <Radio value="2">拒绝</Radio>
              </Tooltip>
            </Radio.Group>
          </Form.Item>
        </Col>
        <Col span={15}>
          <Form.Item name="rejectReason" wrapperCol={{span: 12}} hidden={approveStatus !== "2"}>
            <WdiSelect {...{
              placeholder: "请选择审批退回原因",
              dataLoading: async (params) => {
                const response = await RejectReason.search({formTypeId: props.data.form.formTypeId, isNull: true});
                return response.data.map((item) => {
                  return {value: item.rejectReasonId, label: item.title, content: item.content};
                });
              },
              onChange: (value, option: any) => {
                formRef.current?.setFieldsValue({
                  approvalReason: option ? option['content'] : "",
                });
              }
            }} />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={[0, 0]}
           hidden={!(formRef?.current?.getFieldValue("approveType") == FlowApproveType.InterveneUser || formRef?.current?.getFieldValue("approveType") == FlowApproveType.InterveneError)}>
        <Col span={15}>
          <Form.Item name="approveType" label='干预类型' required>
            <Radio.Group>
              {formRef?.current?.getFieldValue("approveType") == FlowApproveType.InterveneError &&
                <Radio value='1'>错误节点跳过</Radio>}
              {formRef?.current?.getFieldValue("approveType") == FlowApproveType.InterveneUser &&
                <Radio value='2'>指定干预的审批人</Radio>}
            </Radio.Group>
          </Form.Item>
        </Col>
        <Col span={9}>
          <Form.Item name="interveneUserId" wrapperCol={{span: 30}}
                     hidden={!(formRef?.current?.getFieldValue("approveType") == FlowApproveType.InterveneUser)}>
            <WdiSelect
              {...{
                allowClear: false,
                placeholder: '选择当前流程审批人',
                options: currentFlowUsers,
              }} />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={[16, 0]}>
        <Col span={12}>
          <Form.Item name="approvalReason" label="审批意见" labelCol={{span: 24}} wrapperCol={{span: 24}}>
            <Input.TextArea
              rows={2}
              style={{resize: 'vertical'}}
              placeholder="请输入审批意见（拒绝时必填）"
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name="remarkNeedPermission"
            label="财务评估审批意见(仅总监可见)"
            labelCol={{span: 24}}
            wrapperCol={{span: 24}}
            >
              <Input.TextArea
                rows={2}
                style={{resize: 'vertical'}}
                placeholder="请输入审批意见(选填)"
              />
            </Form.Item>
          </Col>
      </Row>
      <WdiTable
        columns={columns}
        showPageRows={3}
        rowKey="uid"
        dataSource={dataSource}
        pagination={false}
        loading={loading}
      />
    </WdiModalForm>
  );
};
export default ApprovalModalApp;
